import { Icon, MenuItem } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { IThemeStorageModel, WorkspaceResourceType } from "common/interfaces";
import { Breadcrumb } from "js/Components/Breadcrumb";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { SharedTheme } from "js/core/models/sharedTheme";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { app } from "js/namespaces";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { SearchBarContainer, UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import getObjectHash from "common/utils/getObjectHash";

const logger = getLogger(LogGroup.TEAMS);

const Container = styled.div`
    background: white;
    padding: 20px;
`;

function TeamSharedThemesPane(props: WorkspaceControllerState) {
    const { resources } = props;

    const themeResources = resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME);

    const sharedThemeIds = themeResources.filter(resource => !resource.metadata.isDefault).map(resource => resource.resourceId);
    const defaultThemeId = themeResources.find(resource => resource.metadata.isDefault)?.resourceId;

    const [defaultTheme, setDefaultTheme] = useState<IThemeStorageModel>(null);
    const [sharedThemes, setSharedThemes] = useState<IThemeStorageModel[]>([]);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        (async () => {
            setFetching(true);
            try {
                const theme = new SharedTheme({ id: defaultThemeId });
                await theme.load();
                await theme.loadTheme();
                setDefaultTheme(theme);
                setFetching(false);
            } catch (error) {
                logger.error(error, "[TeamSharedThemesPane] Error loading default theme");
            }
        })();
    }, [defaultThemeId]);

    useEffect(() => {
        const sharedThemes = sharedThemeIds.map(id => ds.sharedThemes.get(id)).filter(Boolean);
        setSharedThemes(sharedThemes);
    }, [getObjectHash(sharedThemeIds)]);

    const teamThemes: IThemeStorageModel[] = fetching ? [] : [defaultTheme, ...sharedThemes].filter(Boolean);

    const handleCreate = () => {
        AppController.navigateToThemeEditor("new-team-theme");
    };

    const handleEdit = (theme: IThemeStorageModel) => {
        AppController.navigateToThemeEditor(theme.id);
    };

    const handleRename = (theme: IThemeStorageModel) => {
        ShowDialog(InputConfirmationDialog, {
            title: "Rename Theme",
            message: null,
            input: {
                value: theme.get("name")
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: "Renaming theme...",
                        action: async () => {
                            theme.update({ name: name.trim() });
                            await theme.updatePromise;
                        }
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: "Unable to rename theme",
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: "Duplicating theme...",
                action: async () => {
                    const name = "Copy of " + theme.get("name");
                    await app.themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: "Unable to duplicate theme",
                message: err.message
            });
        }
    };

    const handleDelete = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: "Deleting theme...",
                action: async () => {
                    await app.themeManager.deleteTeamTheme(theme.id);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");

            ShowErrorDialog({
                title: "Unable to delete theme",
                message: err.message
            });
        }
    };

    const handleToggleHide = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: theme.get("hidden") ? "Unhiding theme..." : "Hiding theme...",
                action: async () => {
                    theme.update({ hidden: !theme.get("hidden") });
                    await theme.updatePromise;
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: "Unable to toggle hide theme",
                message: err.message
            });
        }
    };

    return (
        <UIPane>
            <SearchBarContainer className="search-container">
                <Breadcrumb>Team Themes</Breadcrumb>
                <RoundIconButton onClick={handleCreate}><Icon>add</Icon></RoundIconButton>
            </SearchBarContainer>
            <UIPaneResultsContainer>
                <FetchingClickShield visible={fetching} />
                <Container>
                    {teamThemes.length > 0 && (
                        <ThumbnailGrid
                            items={teamThemes}
                            columns={4}
                            selectedItem={false}
                            renderThumbnail={(child: { item: IThemeStorageModel }) => {
                                const isDefault = child.item.id === defaultThemeId;
                                return (
                                    <ThemeThumbnail
                                        item={child.item}
                                        isDefault={isDefault && teamThemes.length > 1}
                                        menu={(
                                            <IconMenu>
                                                <MenuItem onClick={() => handleEdit(child.item)}><Icon>edit</Icon>Edit Theme...</MenuItem>
                                                <MenuItem onClick={() => handleRename(child.item)}><Icon>label</Icon>Rename</MenuItem>
                                                <MenuItem onClick={() => handleDuplicate(child.item)}><Icon>content_copy</Icon>Duplicate</MenuItem>
                                                <hr key="divider" />
                                                {child.item.get("hidden")
                                                    ? <MenuItem key="unhide" onClick={() => handleToggleHide(child.item)}><Icon>visibility_on</Icon>Show</MenuItem>
                                                    : <MenuItem key="hide" onClick={() => handleToggleHide(child.item)}><Icon>visibility_off</Icon>Hide</MenuItem>
                                                }
                                                {!isDefault &&
                                                    <MenuItem key="delete" onClick={() => handleDelete(child.item)}><Icon>delete</Icon>Delete...</MenuItem>
                                                }
                                            </IconMenu>
                                        )}
                                        onDoubleClick={() => handleEdit(child.item)}
                                    />
                                );
                            }}
                        />
                    )}
                </Container>
            </UIPaneResultsContainer>
        </UIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamSharedThemesPane);
